import { useState, useCallback, useEffect, useMemo } from 'react'
import { Alert } from '@mui/material'
import { usePOTracking } from '@/hooks/usePOTracking'
import { useAssignableUsers } from '@/hooks/useUsers'
import type { Evidence, InvoiceExtraction, TaxBreakdownItem } from '@/types/invoiceAnalyser'
import {
  DOCUMENT_TYPE_OPTIONS,
  normalizeDocumentType,
  type DocumentType,
} from '@/utils/documentType'
import { useT } from '@/i18n/useT'
import {
  Button,
  GridCell,
  GridHead,
  GridRow,
  Icon,
  IconButton,
  Pill,
  Select,
  Spacer,
  ToggleButton,
  WarningAccordion,
} from '@/components/ui'

interface ExtractionResultProps {
  result: InvoiceExtraction
  /** Opens the audit trail, focused on one field when given */
  onViewEvidence?: (field?: keyof Evidence) => void
  onAddSupplierToWhitelist?: (supplierName: string) => void
  onAddRecipientToWhitelist?: (recipientName: string) => void
  onAddToDatabase?: (result: InvoiceExtraction) => void
  /**
   * Hand the document to Flixpector for review — the same step the Inbox offers,
   * so an invoice that was uploaded here can go through the legal check too. The
   * Bearbeiter is never null: no hand-off happens without one.
   */
  onSendToFlixpector?: (result: InvoiceExtraction, assigneeUserId: string) => void
  /** True while that hand-off is running */
  sendingToFlixpector?: boolean
  onDiscard?: () => void
  /** Name of the analysed file, for the card heading */
  filename?: string | null
}

/** Extraction fields that must be stored as numbers, not strings. */
const NUMERIC_FIELDS: Array<keyof InvoiceExtraction> = [
  'total_net',
  'total_gross',
  'vat_amount',
  'tax_free_amount',
  'taxable_net_amount',
]

const CONFIDENCE_TONE: Record<InvoiceExtraction['confidence'], { fg: string; bg: string; bd: string }> =
  {
    high: { fg: 'var(--kInvFg)', bg: 'var(--kInvBg)', bd: 'var(--kInvBd)' },
    medium: { fg: 'var(--kSrvFg)', bg: 'var(--kSrvBg)', bd: 'var(--kSrvBd)' },
    low: { fg: 'var(--err)', bg: 'var(--errSoft)', bd: 'var(--err)' },
  }

const MAX_PO_DESCRIPTION_LENGTH = 10

const truncateDescription = (description: string | null): string => {
  if (!description) return ''
  const trimmed = description.trim()
  if (!trimmed) return ''
  return trimmed.length > MAX_PO_DESCRIPTION_LENGTH
    ? `${trimmed.slice(0, MAX_PO_DESCRIPTION_LENGTH)}...`
    : trimmed
}

/** The year is part of a PO's identity — numbers repeat across years. */
const formatPOLabel = (po: {
  poNumber: string
  year: number
  description: string | null
}): string => {
  const truncated = truncateDescription(po.description)
  const head = `${po.poNumber} · ${po.year}`
  return truncated ? `${head} - ${truncated}` : head
}

function formatMoney(amount: number | null, currency: string | null, fallback: string): string {
  if (amount == null) return fallback
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
  return `${formatted} ${currency || ''}`.trim()
}

function formatDate(dateStr: string | null, fallback: string): string {
  if (!dateStr) return fallback
  const date = new Date(dateStr)
  return Number.isNaN(date.getTime()) ? dateStr : new Intl.DateTimeFormat('de-DE').format(date)
}

/**
 * One field tile: label, optional whitelist shortcut, the value (static or an
 * input while editing) and the "Beleg" link into the audit trail.
 */
function FieldTile({
  label,
  display,
  raw,
  editing,
  type = 'text',
  onChange,
  onEvidence,
  hasEvidence,
  action,
  muted,
}: {
  label: string
  display: string
  raw?: string | number | null
  editing: boolean
  type?: 'text' | 'number' | 'date'
  onChange?: (value: string) => void
  onEvidence?: () => void
  hasEvidence?: boolean
  action?: React.ReactNode
  /** True when `display` is the "not detected" placeholder rather than a value */
  muted?: boolean
}) {
  const { t } = useT()
  const editable = editing && onChange != null

  return (
    <div
      style={{
        background: 'var(--field)',
        border: '1px solid var(--line2)',
        borderRadius: 8,
        padding: '10px 12px',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--faint)', flex: 1, minWidth: 0 }}>
          {label}
        </div>
        {action}
      </div>

      {editable ? (
        <input
          type={type}
          value={raw ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          step={type === 'number' ? 'any' : undefined}
          style={{
            width: '100%',
            marginTop: 5,
            height: 32,
            background: 'var(--paper)',
            border: '1px solid var(--accentBorder)',
            borderRadius: 6,
            padding: '0 8px',
            color: 'var(--text)',
            fontSize: 13,
            fontWeight: 700,
            outline: 'none',
          }}
        />
      ) : (
        <div
          title={display}
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: muted ? 'var(--faint)' : 'var(--text)',
            marginTop: 5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {display}
        </div>
      )}

      {onEvidence && (
        <button
          type="button"
          className="fx-link-quiet"
          onClick={onEvidence}
          style={{ marginTop: 8 }}
        >
          {hasEvidence ? t('Source') : t('No source')}
        </button>
      )}
    </div>
  )
}

/** Per-rate tax breakdown, in the design's grid-table form. */
function BreakdownTable({
  title,
  items,
  currency,
}: {
  title: string
  items: TaxBreakdownItem[]
  currency: string | null
}) {
  const { t } = useT()
  const template = '2.2fr 1fr 110px 120px'
  const fmt = (n: number | null) =>
    n == null
      ? '–'
      : new Intl.NumberFormat('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(n)

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
        <GridHead
          template={template}
          columns={[
            { key: 'cat', label: t('Category') },
            { key: 'rate', label: `${t('Rate')} %`, align: 'right' },
            { key: 'basis', label: `${t('Basis')} ${currency || ''}`.trim(), align: 'right' },
            { key: 'tax', label: `${t('Tax')} ${currency || ''}`.trim(), align: 'right' },
          ]}
        />
        {items.map((it, i) => (
          <GridRow key={i} template={template} zebra={i % 2 === 1}>
            <GridCell>{it.category_label || it.category_code || '–'}</GridCell>
            <GridCell align="right" color="var(--muted)">
              {it.rate_percent ?? '–'}
            </GridCell>
            <GridCell align="right" color="var(--muted)">
              {fmt(it.tax_basis_amount)}
            </GridCell>
            <GridCell align="right" strong>
              {fmt(it.tax_amount)}
            </GridCell>
          </GridRow>
        ))}
      </div>
    </div>
  )
}

export function ExtractionResult({
  result,
  onViewEvidence,
  onAddSupplierToWhitelist,
  onAddRecipientToWhitelist,
  onAddToDatabase,
  onSendToFlixpector,
  sendingToFlixpector,
  onDiscard,
  filename,
}: ExtractionResultProps) {
  const [showWarnings, setShowWarnings] = useState(true)
  const [showTaxDetails, setShowTaxDetails] = useState(false)
  const [editing, setEditing] = useState(false)
  const [edited, setEdited] = useState<InvoiceExtraction>(result)
  // The PO mapping is only a field on the extraction, like every other one:
  // picking it stores nothing on its own, it travels with whichever of the two
  // actions the user then takes.
  const [selectedPOId, setSelectedPOId] = useState<string>(result.po_id || '')
  // Bearbeiter for the Flixpector hand-off — travels with the document and is
  // stamped on every position over there, exactly as in the Inbox drawer.
  const [assigneeUserId, setAssigneeUserId] = useState('')
  const { users, error: usersError } = useAssignableUsers()
  const { t } = useT()
  /** Shown wherever the extraction produced no value for a field. */
  const notDetected = t('Not detected')

  const { usePOs } = usePOTracking()
  // Every PO from the tracking list, not just the invoice's own year: assigning
  // an invoice charges it against that PO's budget, and a document may well be
  // booked against a PO opened in another year.
  const { data: pos = [], isLoading: posLoading } = usePOs()

  useEffect(() => {
    setEdited(result)
    setSelectedPOId(result.po_id || '')
    setAssigneeUserId('')
    setEditing(false)
  }, [result])

  const handleFieldChange = useCallback((field: keyof InvoiceExtraction, value: string) => {
    setEdited((prev) => {
      if (NUMERIC_FIELDS.includes(field)) {
        const trimmed = value.trim()
        if (trimmed === '') return { ...prev, [field]: null }
        const num = Number(trimmed)
        // Ignore invalid numeric input instead of corrupting the field with a string
        return Number.isNaN(num) ? prev : { ...prev, [field]: num }
      }
      return { ...prev, [field]: value }
    })
  }, [])

  const handleAddToDatabase = useCallback(() => {
    onAddToDatabase?.({ ...edited, po_id: selectedPOId || null })
  }, [edited, selectedPOId, onAddToDatabase])

  const handleSendToFlixpector = useCallback(() => {
    // The button is disabled without one, so this cannot be an empty hand-off
    if (!assigneeUserId) return
    onSendToFlixpector?.({ ...edited, po_id: selectedPOId || null }, assigneeUserId)
  }, [edited, selectedPOId, assigneeUserId, onSendToFlixpector])

  /** Newest year first, so the POs currently being spent against lead the list. */
  const poOptions = useMemo(
    () => [
      {
        value: '',
        label: `${t('Mapping to PO')}: ${
          posLoading
            ? t('loading POs …')
            : pos.length === 0
              ? t('no POs available')
              : t('no PO')
        }`,
      },
      ...[...pos]
        .sort((a, b) => b.year - a.year || a.poNumber.localeCompare(b.poNumber))
        .map((po) => ({ value: po.id, label: `${t('Mapping to PO')}: ${formatPOLabel(po)}` })),
    ],
    [pos, posLoading, t],
  )

  const tone = CONFIDENCE_TONE[result.confidence]
  const evidence = result.evidence

  const ev = (field: keyof Evidence) =>
    onViewEvidence ? { onEvidence: () => onViewEvidence(field), hasEvidence: !!evidence?.[field] } : {}

  return (
    <div
      style={{
        background: 'var(--paper2)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, width: '100%' }}>
          {t('Extracted data')}
          {filename ? ` · ${filename}` : ''}
        </div>
        <Pill fg={tone.fg} bg={tone.bg} border={tone.bd}>
          {t('Confidence')} {t(result.confidence)}
        </Pill>
        <ToggleButton active={editing} onClick={() => setEditing((v) => !v)} height={34}>
          {editing ? t('Stop editing') : t('Edit fields')}
        </ToggleButton>
        <Button variant="ghost" size="sm" onClick={() => setShowTaxDetails((v) => !v)}>
          {showTaxDetails ? t('Hide tax details') : t('Show tax details')}
        </Button>
        <Select<DocumentType>
          value={normalizeDocumentType(edited.document_type)}
          onChange={(v) => setEdited((prev) => ({ ...prev, document_type: v }))}
          ariaLabel={t('Document type')}
          height={34}
          options={DOCUMENT_TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: `${t('Document type')}: ${t(o.label)}`,
          }))}
        />
        {onViewEvidence && (
          <IconButton title={t('Open audit trail')} onClick={() => onViewEvidence()}>
            <Icon name="eye" size={16} />
          </IconButton>
        )}
      </div>

      {edited.currency && edited.currency !== 'EUR' && (
        <div style={{ padding: '12px 18px 0' }}>
          <Alert severity="info">
            {t('Stored in EUR — historical rate of')} {formatDate(edited.invoice_date, notDetected)}
          </Alert>
        </div>
      )}

      <div
        style={{
          padding: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
        }}
      >
        <FieldTile
          label={t('Invoice number')}
          display={edited.invoice_number || notDetected}
          muted={!edited.invoice_number}
          raw={edited.invoice_number}
          editing={editing}
          onChange={(v) => handleFieldChange('invoice_number', v)}
          {...ev('invoice_number')}
        />
        <FieldTile
          label={t('Invoice date')}
          display={formatDate(edited.invoice_date, notDetected)}
          muted={!edited.invoice_date}
          raw={edited.invoice_date}
          type="date"
          editing={editing}
          onChange={(v) => handleFieldChange('invoice_date', v)}
          {...ev('invoice_date')}
        />
        <FieldTile
          label={t('Supplier')}
          display={edited.supplier || notDetected}
          muted={!edited.supplier}
          raw={edited.supplier}
          editing={editing}
          onChange={(v) => handleFieldChange('supplier', v)}
          {...ev('supplier')}
          action={
            edited.supplier && onAddSupplierToWhitelist ? (
              <IconButton
                title={t('Add to the supplier whitelist')}
                size="sm"
                onClick={() => onAddSupplierToWhitelist(edited.supplier!)}
              >
                <Icon name="plus" size={13} strokeWidth={2.4} style={{ color: 'var(--accent)' }} />
              </IconButton>
            ) : undefined
          }
        />
        <FieldTile
          label={t('Recipient')}
          display={edited.recipient || notDetected}
          muted={!edited.recipient}
          raw={edited.recipient}
          editing={editing}
          onChange={(v) => handleFieldChange('recipient', v)}
          {...ev('recipient')}
          action={
            edited.recipient && onAddRecipientToWhitelist ? (
              <IconButton
                title={t('Add to the recipient whitelist')}
                size="sm"
                onClick={() => onAddRecipientToWhitelist(edited.recipient!)}
              >
                <Icon name="plus" size={13} strokeWidth={2.4} style={{ color: 'var(--accent)' }} />
              </IconButton>
            ) : undefined
          }
        />
        <FieldTile
          label={t('Currency')}
          display={edited.currency || notDetected}
          muted={!edited.currency}
          raw={edited.currency}
          editing={editing}
          onChange={(v) => handleFieldChange('currency', v)}
          {...ev('currency')}
        />
        <FieldTile
          label={t('Net')}
          display={formatMoney(edited.total_net, edited.currency, notDetected)}
          muted={edited.total_net == null}
          raw={edited.total_net}
          type="number"
          editing={editing}
          onChange={(v) => handleFieldChange('total_net', v)}
          {...ev('total_net')}
        />
        <FieldTile
          label={t('VAT')}
          display={formatMoney(edited.vat_amount, edited.currency, notDetected)}
          muted={edited.vat_amount == null}
          raw={edited.vat_amount}
          type="number"
          editing={editing}
          onChange={(v) => handleFieldChange('vat_amount', v)}
          {...ev('vat_amount')}
        />
        <FieldTile
          label={t('Gross')}
          display={formatMoney(edited.total_gross, edited.currency, notDetected)}
          muted={edited.total_gross == null}
          raw={edited.total_gross}
          type="number"
          editing={editing}
          onChange={(v) => handleFieldChange('total_gross', v)}
          {...ev('total_gross')}
        />
        <FieldTile
          label={t('VAT ID')}
          display={edited.ust_id || notDetected}
          muted={!edited.ust_id}
          raw={edited.ust_id}
          editing={editing}
          onChange={(v) => handleFieldChange('ust_id', v)}
        />
        {edited.project_name !== null && (
          <FieldTile
            label={t('Project')}
            display={edited.project_name || notDetected}
            muted={!edited.project_name}
            raw={edited.project_name}
            editing={editing}
            onChange={(v) => handleFieldChange('project_name', v)}
            {...ev('project_name')}
          />
        )}

        {showTaxDetails && (
          <>
            <FieldTile
              label={t('Tax-free')}
              display={formatMoney(edited.tax_free_amount, edited.currency, notDetected)}
              muted={edited.tax_free_amount == null}
              raw={edited.tax_free_amount}
              type="number"
              editing={editing}
              onChange={(v) => handleFieldChange('tax_free_amount', v)}
              {...ev('tax_free_amount')}
            />
            <FieldTile
              label={t('Taxable net')}
              display={formatMoney(edited.taxable_net_amount, edited.currency, notDetected)}
              muted={edited.taxable_net_amount == null}
              raw={edited.taxable_net_amount}
              type="number"
              editing={editing}
              onChange={(v) => handleFieldChange('taxable_net_amount', v)}
              {...ev('taxable_net_amount')}
            />
          </>
        )}
      </div>

      <div style={{ padding: '0 18px 18px' }}>
        {showTaxDetails && edited.vat_breakdown && edited.vat_breakdown.length > 0 && (
          <BreakdownTable
            title={t('VAT breakdown')}
            items={edited.vat_breakdown}
            currency={edited.currency}
          />
        )}
        {showTaxDetails && edited.tax_free_breakdown && edited.tax_free_breakdown.length > 0 && (
          <BreakdownTable
            title={t('Tax-free breakdown')}
            items={edited.tax_free_breakdown}
            currency={edited.currency}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 2px 0', flexWrap: 'wrap' }}>
          <Spacer />
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {t('Net')} {formatMoney(edited.total_net, edited.currency, notDetected)} · {t('VAT')}{' '}
            {formatMoney(edited.vat_amount, edited.currency, notDetected)}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {formatMoney(edited.total_gross, edited.currency, notDetected)}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <WarningAccordion
            items={edited.warnings ?? []}
            open={showWarnings}
            onToggle={() => setShowWarnings((v) => !v)}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginTop: 18,
            flexWrap: 'wrap',
          }}
        >
          <Spacer />
          {onDiscard && (
            <Button variant="ghost" onClick={onDiscard}>
              {t('Discard')}
            </Button>
          )}
          {/* Sits between Discard and the PO mapping: the review comes before
              the invoice is booked, so this is the step that precedes both. */}
          {onSendToFlixpector && (
            <>
              {/* The requirement lives in the control itself and in the disabled
                  button — no caption line, so both selects sit level with the
                  buttons in one row. */}
              <Select<string>
                value={assigneeUserId}
                onChange={setAssigneeUserId}
                ariaLabel={t('Handler')}
                options={[
                  {
                    value: '',
                    label: `${t('Handler')}: ${
                      usersError ? t('list not loaded') : t('please choose')
                    }`,
                  },
                  ...users.map((u) => ({
                    value: u.id,
                    label: `${t('Handler')}: ${
                      u.initials ? `${u.fullName} (${u.initials})` : u.fullName
                    }`,
                  })),
                ]}
              />
              <Button
                variant="outline"
                onClick={handleSendToFlixpector}
                disabled={sendingToFlixpector || !assigneeUserId}
                title={
                  assigneeUserId
                    ? t(
                        'Hand over to Flixpector for review — then appears in the Inbox under "With Flixpector"',
                      )
                    : t('Choose a handler first')
                }
              >
                {sendingToFlixpector ? t('Handing over …') : t('Push to Flixpector')}
              </Button>
            </>
          )}
          <Select<string>
            value={selectedPOId}
            onChange={setSelectedPOId}
            ariaLabel={t('Mapping to PO')}
            options={poOptions}
          />
          {onAddToDatabase && (
            <Button variant="accent" onClick={handleAddToDatabase}>
              {t('Add to database')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
